// Utility for notification sounds and incoming message alert rings

export interface NotificationSound {
  id: string;
  name: string;
  src: string;
}

export const NOTIFICATION_SOUNDS: NotificationSound[] = [
  { id: 'sound1', name: 'Pop', src: '/sounds/notification_1.mp3' },
  { id: 'sound2', name: 'Ding', src: '/sounds/notification_2.mp3' },
  { id: 'sound3', name: 'Chime', src: '/sounds/notification_3.mp3' },
  { id: 'sound4', name: 'Ping', src: '/sounds/notification_4.mp3' },
  { id: 'sound5', name: 'Glass', src: '/sounds/notification_5.mp3' },
];

let lastPlayedAt = 0;

/**
 * Checks whether alert ring on incoming messages is enabled.
 * Defaults to true if not explicitly set to 'false'.
 */
export function getAlertRingEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  const alertRing = localStorage.getItem('alertRingEnabled');
  if (alertRing !== null) return alertRing === 'true';
  const soundVal = localStorage.getItem('soundEnabled');
  if (soundVal !== null) return soundVal === 'true';
  return true;
}

/**
 * Updates the alert ring preference in localStorage and dispatches a window event.
 */
export function setAlertRingEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem('alertRingEnabled', String(enabled));
  localStorage.setItem('soundEnabled', String(enabled));
  window.dispatchEvent(new CustomEvent('alertRingEnabledChange', { detail: enabled }));
}

/**
 * Returns the currently selected sound ID.
 */
export function getSelectedSound(): string {
  if (typeof window === 'undefined') return 'sound1';
  return localStorage.getItem('selectedSound') || 'sound1';
}

/**
 * Sets the selected sound ID in localStorage and dispatches a window event.
 */
export function setSelectedSound(soundId: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem('selectedSound', soundId);
  window.dispatchEvent(new CustomEvent('selectedSoundChange', { detail: soundId }));
}

/**
 * Plays the notification alert ring tone.
 * @param soundId Optional specific sound ID to play (e.g. for previewing in dropdown).
 * @param force If true, bypasses the enabled check and throttle (useful for user preview clicks).
 */
export function playAlertRing(soundId?: string, force = false): void {
  if (typeof window === 'undefined') return;

  const isEnabled = getAlertRingEnabled();
  if (!isEnabled && !force) return;

  const now = Date.now();
  if (!force && now - lastPlayedAt < 1200) return;
  if (!force) {
    lastPlayedAt = now;
  }

  const targetSoundId = soundId || getSelectedSound();
  const sound = NOTIFICATION_SOUNDS.find((s) => s.id === targetSoundId) || NOTIFICATION_SOUNDS[0];

  try {
    const audio = new Audio(sound.src);
    audio.volume = 0.7;
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch((err) => {
        console.warn('Alert ring audio play prevented by browser policy:', err);
      });
    }
  } catch (e) {
    console.error('Failed to play alert ring:', e);
  }
}
